/**
 * Stateful adapters. These hold only SCENE-LOCAL memory — an accent latch, a
 * slice cursor, the platter's angle history. Every discrete gesture decision
 * (pinch, fist, degree, octave, latch) arrives already made, in the
 * ControlState produced by the one pipeline in main.ts; an adapter that
 * re-derived one from raw Features would be a second, disagreeing
 * implementation of the vocabulary, which is exactly what this split removes.
 *
 * Still no audio and no DOM imports here — an adapter returns an intent, it
 * does not apply one.
 */

import { degreeFromHeight } from '../audio/common/pitch';
import { rateFromAngles } from './angular';
import { hysteresis, rising } from './filters';
import { mapArp, mapBass, mapBreaks, mapScratch, mapTala, mapWarble, type WarbleMode } from './mappers';
import type {
  ArpIntent, BassIntent, BreaksIntent, ControlState, FeatureFrame, ScratchIntent, TalaIntent,
  WarbleIntent,
} from './types';

export class BassAdapter {
  private accent = false;
  reset(): void { this.accent = false; }
  update(frame: FeatureFrame, control: ControlState): BassIntent {
    // Accent is a rising threshold on spread; a bare `> 0.7` flapped at the
    // boundary. It only latches into a note at trigger time, but the intent
    // should still be stable frame to frame.
    this.accent = rising(frame.expr.spread, this.accent, 0.7, 0.6);
    return mapBass(frame, {
      trigger: control.triggers.some((t) => t.kind === 'note'),
      accent: this.accent,
      degree: control.degree,
      octave: control.octave,
    });
  }
}

export class ArpAdapter {
  private running = false;
  private octaves = 1;
  reset(): void { this.running = false; this.octaves = 1; }
  setRunning(v: boolean): void { this.running = v; }
  update(frame: FeatureFrame, control: ControlState): ArpIntent {
    // Octave span is a 3-band quantizer over spread. Unhysteresed, a hand
    // parked at a band edge flipped it every frame — and every flip rebuilt
    // the sequence and reset it to step 0, restarting the arp at 60 Hz. Reuse
    // the degree-boundary hysteresis: 3 bands, 0.25 band-steps of dead travel.
    this.octaves = 1 + degreeFromHeight(frame.expr.spread, this.octaves - 1, 3, 0.25);
    return mapArp(frame, { degree: control.degree, running: this.running, octaves: this.octaves });
  }
}

export class BreaksAdapter {
  private stutter = false;
  reset(): void { this.stutter = false; }
  /**
   * Height picks the slice WITHIN the bar (control.degree, already quantized
   * into slicesPerBar zones by the pipeline); the bar comes from the scene's
   * chips. The old behaviour — a hidden cursor advanced by each pinch — meant
   * the slice you got depended on how many times you had pinched since scene
   * load: unpredictable by construction, and the device test said so.
   */
  update(frame: FeatureFrame, control: ControlState, slicesPerBar: number, bar = 0): BreaksIntent {
    const inBar = Math.max(0, Math.min(slicesPerBar - 1, control.degree));
    const selectedSlice = bar * slicesPerBar + inBar;
    const triggerSlice = control.triggers.some((t) => t.kind === 'note') ? selectedSlice : -1;
    // A deep pinch, below the note threshold. Closing hysteresis 0.2/0.3 —
    // the old bare `< 0.2` sat un-debounced on the axis's noisiest region.
    this.stutter = hysteresis(frame.expr.aperture, this.stutter, 0.2, 0.3);
    return mapBreaks(frame, { selectedSlice, triggerSlice, stutter: this.stutter });
  }
}

/**
 * The scratch adapter — the one with real memory. It holds the previous angle
 * and the current rate; every value it produces comes out of angular.ts. The
 * grab is the vocabulary's pinch: this adapter watches control.pinch
 * transitions rather than re-deriving a press from raw aperture.
 */
export class ScratchAdapter {
  private prevAngle: number | null = null;
  private prevPinch = false;
  private rate = 1;
  private held = false;

  reset(): void { this.prevAngle = null; this.prevPinch = false; this.rate = 1; this.held = false; }

  get current(): number { return this.rate; }
  get isHeld(): boolean { return this.held; }

  update(frame: FeatureFrame, control: ControlState, cut: boolean): ScratchIntent {
    const angle = frame.expr.angle;
    if (control.pinch && !this.prevPinch) { this.held = true; this.prevAngle = angle; }
    if (!control.pinch && this.prevPinch) { this.held = false; this.prevAngle = null; }
    this.prevPinch = control.pinch;

    const prev = this.prevAngle ?? angle;
    this.rate = rateFromAngles(this.rate, prev, angle, frame.dt, this.held);
    if (this.held) this.prevAngle = angle;
    return mapScratch(frame, { rate: this.rate, cut });
  }
}

export class TalaAdapter {
  private running = false;
  reset(): void { this.running = false; }
  setRunning(v: boolean): void { this.running = v; }
  update(frame: FeatureFrame, control: ControlState): TalaIntent {
    return mapTala(frame, {
      running: this.running,
      strike: control.triggers.some((t) => t.kind === 'note'),
      degree: control.degree,
    });
  }
}

/**
 * The warble adapter: holds the PERCH/PLANE mode, the patch's baseline beta
 * (one-hand play), and the previous fist so the scene can flip the structural
 * refl param on the EDGE only — refl is setValueAtTime territory, and writing
 * it every frame would be ninety-six events, not four.
 */
export class WarbleAdapter {
  private mode: WarbleMode = 'perch';
  private fallbackBeta = 1.0;
  private prevFist = false;
  reset(): void { this.prevFist = false; }
  setMode(m: WarbleMode): void { this.mode = m; }
  getMode(): WarbleMode { return this.mode; }
  setFallbackBeta(b: number): void { this.fallbackBeta = b; }
  /** True exactly once per fist engage/release — the refl edge. */
  fistEdge(control: ControlState): boolean {
    const changed = control.fist !== this.prevFist;
    this.prevFist = control.fist;
    return changed;
  }
  update(frame: FeatureFrame, control: ControlState): WarbleIntent {
    return mapWarble(frame, {
      mode: this.mode,
      degree: control.degree,
      fallbackBeta: this.fallbackBeta,
      fist: control.fist,
      trigger: control.triggers.some((t) => t.kind === 'note'),
    });
  }
}
